import { LifecycleState, SharedThing } from '@eagle/core-data-types';
import { Chip, styled } from '@ethings/ui-toolkit';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaybePropertyValue } from '../../format';

const EXPIRING_SOON_DAYS = 14;

interface LeaseStatusColumnProps {
  sharedThing: SharedThing & { lifecycleState: (LifecycleState & { stageDisplay: string }) | null };
}

const StyledChip = styled(Chip)(({ theme }) => ({
  '& .MuiChip-label': {
    maxWidth: '100%',
    fontWeight: 500,
    fontSize: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8125rem',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    minHeight: 32,
  },
}));

export const LeaseStatusColumn: FC<LeaseStatusColumnProps> = ({ sharedThing }) => {
  const { t } = useTranslation(['admin']);

  const startDate = getMaybePropertyValue<string>(sharedThing.lifecycleState?.stageProperties?.loan?.startDate);
  const finishDate = getMaybePropertyValue<string>(sharedThing.lifecycleState?.stageProperties?.loan?.finishDate);
  const leaseStatus = (startDate && finishDate) ? getLeaseStatus(startDate, finishDate) : undefined;

  const getColor = (): 'default' | 'success' | 'error' | 'warning' => {
    if (!leaseStatus) {
      return 'default';
    }
    switch (leaseStatus.status) {
      case 'ACTIVE':
        return 'success';
      case 'EXPIRED':
        return 'error';
      case 'EXPIRING_SOON':
        return 'warning';
      case 'NOT_STARTED':
        return 'default';
    }
  };

  const getStageDisplay = (): string => {
    const stageId = sharedThing.lifecycleState?.stageId;
    const stageDisplay = sharedThing.lifecycleState?.stageDisplay ?? '';
    if (stageId) {
      const key = `page.lease-management.stages.${stageId}`;
      const translated = t(key, { ns: 'admin' });
      if (translated !== key) return translated;
    }
    return stageDisplay;
  };

  const getLabel = (): string => {
    if (!leaseStatus) {
      return getStageDisplay();
    }
    switch (leaseStatus.status) {
      case 'ACTIVE':
        return t('admin:page.thing-details.loan-management.active.labels');
      case 'EXPIRED':
        return leaseStatus.daysExpired > 0 ? t('admin:page.thing-details.loan-management.expired-days-ago.labels', { count: leaseStatus.daysExpired }) : t('admin:page.thing-details.loan-management.expired.labels');
      case 'EXPIRING_SOON':
        return t('admin:page.thing-details.loan-management.expiring-soon.labels');
      case 'NOT_STARTED':
        return t('admin:page.thing-details.loan-management.not-started.labels');
    }
  };

  return (
    <StyledChip
      data-testid="lease-stage-badge"
      label={getLabel()}
      size="small"
      color={getColor()}
    />
  );
};

type LeaseStatus =
  | { status: 'NOT_STARTED' }
  | { status: 'ACTIVE' }
  | { status: 'EXPIRING_SOON' }
  | { status: 'EXPIRED'; daysExpired: number }

const getLeaseStatus = (
  startDate: string,
  finishDate: string,
): LeaseStatus => {
  const now = DateTime.now();
  const startDateTime = DateTime.fromISO(startDate);
  const finishDateTime = DateTime.fromISO(finishDate);
  const daysToFinish = finishDateTime.diff(now, 'days').as('days');
  const daysSinceFinish = now.diff(finishDateTime, 'days').as('days');

  if (now < startDateTime) {
    return { status: 'NOT_STARTED' };
  }

  if (now > finishDateTime) {
    return { status: 'EXPIRED', daysExpired: Math.floor(daysSinceFinish) };
  }

  if (daysToFinish <= EXPIRING_SOON_DAYS) {
    return { status: 'EXPIRING_SOON' };
  }

  return { status: 'ACTIVE' };
};
